import { apiUrls } from "../../actprep-ui/utils/urls";
import Box from "@mui/material/Box";
import PaginatedTable from "../../actprep-ui/components/PaginatedTable";
import { sortingModes } from "../../actprep-ui/constants/dataGrid";
import { useGet } from "../../actprep-ui/hooks/api";
import useFilters from "../../actprep-ui/hooks/ui/useFilters";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Stack } from "@mui/material";
import {
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  Paper,
} from "@mui/material";
import { Add, Tune } from "@mui/icons-material";
import useDisclosure from "../../actprep-ui/hooks/ui/useDisclosure";
import ToDoTemplateFormDialog from "../../actprep-ui/components/ToDoTemplateFormDialog";
import { useMemo, useState } from "react";
import api from "../../actprep-ui/utils/api";
import { showSuccessSnackbar } from "../../actprep-ui/utils/feedback";
import ToDoEventTriggerForm from "../ToDoEventTriggerForm";
import dayjs from "dayjs";
import { formatRecurrenceRule } from "../../actprep-ui/utils/formatting";
import ToDoEventForm from "../ToDoEventForm";
import { useConfirm } from "material-ui-confirm";
import { filterTypes } from "../../actprep-ui/constants/ui";

const toDoEventAudienceFilters = [
  {
    field: "roles__in",
    type: filterTypes.MODEL_AUTOCOMPLETE,
    props: {
      modelName: "Role",
      label: "Role",
      multiple: true,
    },
  },
  {
    field: "teams__in",
    type: filterTypes.MODEL_AUTOCOMPLETE,
    props: {
      modelName: "Team",
      label: "Team",
      multiple: true,
    },
  },
];

const getAudienceLabel = (item) => item?.label || item?.title || item?.name || "";

const getTriggerSortDate = (trigger) => {
  const sortDate = trigger.repeating
    ? trigger.repeat_start_date || trigger.next_occurrence_date || trigger.date
    : trigger.date;
  return sortDate ? dayjs(sortDate).valueOf() : Number.POSITIVE_INFINITY;
};

const renderAudienceChips = (audiences) => {
  if (!audiences.length) {
    return null;
  }
  return (
    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
      {audiences.map((audience, index) => (
        <Chip
          key={`${audience.type}-${audience.title}-${index}`}
          size="small"
          variant="outlined"
          label={`${audience.title}: ${audience.inclusions.join(", ")}`}
        />
      ))}
    </Stack>
  );
};

function ToDoEventMasterDetail({ row }) {
  const { data: templateData, refetch: refetchTemplates } = useGet(
    apiUrls.admin.toDoTemplates(),
    {
      params: {
        search_params: { event_id: row.uuid },
        select_related: ['event'],
        prefetch_related: ["roles", "teams", 'user_profiles', 'user_profiles__user'],
        exclude: ['created_by', 'roles__team'],
        keep_related: true,
        order_by: ["event_occurrence_offset"],
      },
    }
  );
  const { data: triggerData, refetch: refetchTriggers } = useGet(
    apiUrls.admin.toDoEventTriggers(),
    {
      params: {
        search_params: { event_id: row.uuid },
      },
    }
  );
  const { data: triggerAudienceOptionData } = useGet(apiUrls.admin.toDoEventTriggers(), {
    params: {
      search_params: { event_id: row.uuid },
      include_audience_options: true,
    },
  });
  const [selectedToDoTemplate, setSelectedToDoTemplate] = useState(null);
  const toDoDisc = useDisclosure();
  const addTriggerDisc = useDisclosure();
  const filterDisc = useDisclosure();
  const { filters, Filters, FiltersAppliedList } = useFilters({
    name: "ToDoEvent Master Detail Filters",
    filtersConfig: toDoEventAudienceFilters,
    handleClose: filterDisc.onClose,
  });

  const confirm = useConfirm();
  const toDoTemplates = templateData?.data || [];
  const sortedToDoEventTriggers = useMemo(
    () =>
      [...(triggerData?.data || [])].sort(
        (a, b) => getTriggerSortDate(a) - getTriggerSortDate(b)
      ),
    [triggerData?.data]
  );
  const triggerAudienceOptions = triggerAudienceOptionData?.data || {
    roles: [],
    teams: [],
  };
  const triggerAudienceMaps = {
    roles: new Map(
      (triggerAudienceOptions.roles || []).map((role) => [role.uuid, role])
    ),
    teams: new Map(
      (triggerAudienceOptions.teams || []).map((team) => [team.uuid, team])
    ),
  };
  const roleFilterIds = new Set(
    (filters.roles__in || []).map((roleOption) => roleOption.uuid)
  );
  const teamFilterIds = new Set(
    (filters.teams__in || []).map((teamOption) => teamOption.uuid)
  );
  const filteredToDoTemplates = toDoTemplates.filter((template) => {
    const templateRoleIds = new Set((template.roles || []).map((role) => role.uuid));
    const templateTeamIds = new Set((template.teams || []).map((team) => team.uuid));
    const rolesMatch =
      roleFilterIds.size === 0 ||
      [...roleFilterIds].some((roleId) => templateRoleIds.has(roleId));
    const teamsMatch =
      teamFilterIds.size === 0 ||
      [...teamFilterIds].some((teamId) => templateTeamIds.has(teamId));
    return rolesMatch && teamsMatch;
  });

  // Placeholder callbacks—you'll hook these up to real handlers later
  const handleEditTemplate = (template) => {
    setSelectedToDoTemplate(template);
    toDoDisc.onOpen();
  };
  const handleAddTemplate = () => {
    setSelectedToDoTemplate(null);
    toDoDisc.onOpen();
  };
  const handleDeleteTemplate = async (template) => {
    confirm({
      title: "Delete To-Do Template",
      description: "Are you sure you want to delete this to-do template?",
    })
      .then(async () => {
        const response = await api.delete(
          apiUrls.admin.toDoTemplate(template.uuid)
        );
        if (response.data?.success) {
          showSuccessSnackbar("Template deleted!");
          refetchTemplates();
        }
      })
      .catch(() => {});
  };
  const handleDeleteTrigger = async (trigger) => {
    confirm({
      title: "Delete Trigger",
      description: "Are you sure you want to delete this trigger?",
    })
      .then(async () => {
        const response = await api.delete(
          apiUrls.admin.toDoEventTriggers(trigger.uuid)
        );
        if (response.data?.success) {
          showSuccessSnackbar("Trigger deleted!");
          refetchTriggers();
        }
      })
      .catch(() => {});
  };

  const getTemplateAudienceChips = (template) => {
    const roleAudience = (template.roles || [])
      .map((role) => getAudienceLabel(role))
      .filter(Boolean);
    const teamAudience = (template.teams || [])
      .map((team) => getAudienceLabel(team))
      .filter(Boolean);
    const audiences = [];
    if (roleAudience.length) {
      audiences.push({ type: "roles", title: "Roles", inclusions: roleAudience });
    }
    if (teamAudience.length) {
      audiences.push({ type: "teams", title: "Teams", inclusions: teamAudience });
    }
    return audiences;
  };

  const getTriggerInclusionChips = (trigger) => {
    const audienceInclusions = trigger?.audience_inclusions || {};
    const audiences = [];

    ["roles", "teams"].forEach((audienceType) => {
      const inclusionsByGroup = audienceInclusions[audienceType] || {};
      Object.entries(inclusionsByGroup).forEach(([groupId, userIds]) => {
        const group = triggerAudienceMaps[audienceType].get(groupId);
        const userMap = new Map((group?.users || []).map((user) => [user.uuid, user]));
        const includedUserLabels = (userIds || [])
          .map((userId) => getAudienceLabel(userMap.get(userId)) || userId)
          .filter(Boolean);
        if (includedUserLabels.length) {
          audiences.push({
            type: audienceType,
            title: group?.title || groupId,
            inclusions: includedUserLabels,
          });
        }
      });
    });

    return audiences;
  };

  return (
    <>
      <Drawer anchor="right" {...filterDisc.getDisclosureProps()}>
        <Box sx={{ width: "25rem", pt: 2 }}>
          <Filters />
        </Box>
      </Drawer>
      <ToDoTemplateFormDialog
        {...toDoDisc.getDisclosureProps()}
        initialToDoEvent={row}
        initialEventOccurrenceOffset={
          selectedToDoTemplate?.event_occurrence_offset ?? 0
        }
        toDoTemplateId={selectedToDoTemplate?.uuid}
        onSubmit={() => {
          refetchTemplates();
          toDoDisc.onClose();
        }}
      />
      <Dialog {...addTriggerDisc.getDisclosureProps()}>
        <DialogTitle>Add Event Trigger</DialogTitle>
        <DialogContent>
          <ToDoEventTriggerForm
            onSubmit={() => {
              addTriggerDisc.onClose();
              refetchTriggers();
            }}
            eventId={row.uuid}
          />
        </DialogContent>
      </Dialog>
      <Box
        sx={{
          p: 2,
        }}
      >
        <Box
          component={Paper}
          sx={{
            p: 2,
          }}
          elevation={2}
        >
          {/* Templates Section */}
          <Box sx={{ mt: 4, mb: 1, display: "flex", alignItems: "center" }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }} fontWeight={"bold"}>
              To-do Items
            </Typography>
            <Chip
              {...filterDisc.getButtonProps()}
              label="Filters"
              icon={<Tune />}
              size="small"
              sx={{ mr: 1 }}
            />
            <Chip
              onClick={handleAddTemplate}
              label="Add Item"
              icon={<Add />}
              size="small"
            />
          </Box>
          <FiltersAppliedList />
          <List disablePadding dense>
            {filteredToDoTemplates.length > 0 ? (
              filteredToDoTemplates.map((tpl) => (
                <ListItem
                  key={tpl.uuid}
                  secondaryAction={
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        edge="end"
                        aria-label="edit"
                        onClick={() => handleEditTemplate(tpl)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleDeleteTemplate(tpl)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  }
                  divider
                >
                  <ListItemText
                    primary={tpl.title}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Offset: {tpl.event_occurrence_offset} day
                          {tpl.event_occurrence_offset === 1 ? "" : "s"}
                        </Typography>
                        {renderAudienceChips(getTemplateAudienceChips(tpl))}
                      </Box>
                    }
                  />
                </ListItem>
              ))
            ) : (
              <ListItem>
                <ListItemText
                  primary={
                    toDoTemplates.length
                      ? "No to-do items match current role/team filters."
                      : "No to-do items added."
                  }
                />
              </ListItem>
            )}
          </List>

          {/* Triggers Section */}
          <Box sx={{ mt: 4, mb: 1, display: "flex", alignItems: "center" }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }} fontWeight={"bold"}>
              Triggers
            </Typography>
            <Chip
              {...addTriggerDisc.getButtonProps()}
              label="Add Trigger"
              icon={<Add />}
              size="small"
            />
          </Box>
          <List disablePadding dense>
            {sortedToDoEventTriggers.length > 0 ? (
              sortedToDoEventTriggers.map((tr) => (
                <ListItem
                  key={tr.uuid}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleDeleteTrigger(tr)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                  divider
                >
                  <ListItemText
                    primary={tr.title}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {tr.repeating
                            ? formatRecurrenceRule(tr.recurrence)
                            : dayjs(tr.date).toDate().toLocaleDateString()}
                        </Typography>
                        {renderAudienceChips(getTriggerInclusionChips(tr))}
                      </Box>
                    }
                  />
                </ListItem>
              ))
            ) : (
              <ListItem>
                <ListItemText primary="No triggers scheduled." />
              </ListItem>
            )}
          </List>
        </Box>
      </Box>
    </>
  );
}

export default function ToDoEventsTable() {
  const columns = [
    {
      field: "title",
      headerName: "Title",
      width: 150,
    },
    {
      field: "trigger_count",
      headerName: "Triggers",
      width: 100,
    },
    {
      field: "to_do_templates_count",
      headerName: "Items",
      width: 100,
    },
  ];

  return (
    <>
      <Box sx={{ height: "100%" }}>
        <PaginatedTable
          searchable
          tableName={"ToDoEventManagement"}
          addRowButtonText="Add Event"
          apiUrl={apiUrls.admin.toDoEvents()}
          columns={columns}
          urlParams={{
            keep_related: true,
            related_as_options: true,
          }}
          initialState={{
            pinnedColumns: {
              right: ["actions"],
            },
          }}
          sortingMode={sortingModes.SERVER}
          disableCellFocus
          getRowTitle={({ row }) => row?.title}
          getDetailPanelContent={({ row }) => (
            <ToDoEventMasterDetail row={row} />
          )}
          getDetailPanelHeight={() => "auto"}
          CreationForm={ToDoEventForm}
        />
      </Box>
    </>
  );
}
